<template>
  <div class="cms-map-widget" :class="classModifiers">
    <div v-if="hasError" class="cms-map-widget__message">Unable to load this map right now.</div>
    <template v-else>
      <div v-if="showSummary" class="cms-map-widget__summary">
        <component
          :is="summaryHeadlineUrl ? 'a' : 'span'"
          v-if="summaryHeadlineParts"
          class="cms-map-widget__headline"
          :href="summaryHeadlineUrl || undefined"
        >
          <span
            v-for="(part, index) in summaryHeadlineParts"
            :key="index"
            :class="
              part.accent && part.party ? 'cms-map-widget__headline-accent--' + part.party : null
            "
            :style="part.accent && part.color ? { color: part.color } : null"
            >{{ part.text }}</span
          >
        </component>
        <div v-else-if="mapTitle" class="cms-map-widget__title">
          {{ mapTitle }}
        </div>
        <div v-if="summarySubheader" class="cms-map-widget__summary-subheader">
          <span>{{ summarySubheader }}</span>
          <span
            v-if="summaryHeadlineParts"
            ref="summaryInfo"
            class="cms-map-widget__summary-info-wrap"
            @keydown.esc="showSummaryInfo = false"
          >
            <button
              class="cms-map-widget__summary-info"
              type="button"
              :aria-label="summaryInfoText"
              :aria-expanded="showSummaryInfo ? 'true' : 'false'"
              @click="toggleSummaryInfo"
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.3" />
                <circle cx="8" cy="4.9" r="0.95" fill="currentColor" />
                <path
                  d="M8 7.2v4.6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <span
              v-if="showSummaryInfo"
              class="cms-map-widget__summary-info-popover"
              role="tooltip"
            >
              {{ summaryInfoText }}
              <span
                v-if="keyInSummaryInfo && noMarketKeyItems.length > 0"
                class="cms-map-widget__key cms-map-widget__key--popover"
              >
                <span
                  v-for="item in noMarketKeyItems"
                  :key="item.key"
                  class="cms-map-widget__key-item"
                >
                  <span
                    class="cms-map-widget__key-swatch"
                    :style="{ background: item.swatch }"
                  ></span
                  >{{ item.label }}
                </span>
              </span>
            </span>
          </span>
        </div>
        <div v-if="showElectionToolbar" class="cms-map-widget__toolbar">
          <div ref="toolbarLabels" class="cms-map-widget__toolbar-labels">
            <div class="cms-map-widget__toolbar-label cms-map-widget__toolbar-label--democratic">
              <span class="cms-map-widget__toolbar-count">{{
                democraticToolbarCounter.total
              }}</span>
              <span class="cms-map-widget__toolbar-party">Democratic</span>
              <span v-if="democraticToolbarHint" class="cms-map-widget__toolbar-hint">{{
                democraticToolbarHint
              }}</span>
            </div>
            <div
              v-for="item in toolbarMiddleLabels"
              :key="item.key"
              ref="toolbarMiddleLabel"
              class="cms-map-widget__toolbar-label cms-map-widget__toolbar-label--middle"
              :class="'cms-map-widget__toolbar-label--' + item.bucket"
              :data-center="item.center"
              :style="{ left: item.center + '%' }"
            >
              <span class="cms-map-widget__toolbar-middle-count">{{ item.value }}</span>
              <span class="cms-map-widget__toolbar-party">{{ item.label }}</span>
            </div>
            <div class="cms-map-widget__toolbar-label cms-map-widget__toolbar-label--republican">
              <span v-if="republicanToolbarHint" class="cms-map-widget__toolbar-hint">{{
                republicanToolbarHint
              }}</span>
              <span class="cms-map-widget__toolbar-party">Republican</span>
              <span class="cms-map-widget__toolbar-count">{{
                republicanToolbarCounter.total
              }}</span>
            </div>
          </div>
          <div class="cms-map-widget__toolbar-bar">
            <div
              v-for="segment in toolbarSegments"
              :key="segment.key"
              class="cms-map-widget__toolbar-segment"
              :class="[
                'cms-map-widget__toolbar-segment--' + segment.party,
                { 'cms-map-widget__toolbar-segment--striped': segment.striped },
              ]"
              :style="{ width: segment.width + '%', backgroundColor: segment.color }"
              :title="getToolbarSegmentTitle(segment)"
            ></div>
          </div>
          <div v-if="showMajorityLabel || hasCurrentSeats" class="cms-map-widget__toolbar-majority">
            <span
              v-if="currentSeats.democratic !== null"
              class="cms-map-widget__toolbar-current cms-map-widget__toolbar-current--democratic"
              >{{ currentSeats.democratic }} current</span
            >
            <template v-if="showMajorityLabel">
              <span class="cms-map-widget__toolbar-majority-tick"></span>
              <span class="cms-map-widget__toolbar-majority-caption">{{ majorityCaption }}</span>
            </template>
            <span
              v-if="currentSeats.republican !== null"
              class="cms-map-widget__toolbar-current cms-map-widget__toolbar-current--republican"
              >{{ currentSeats.republican }} current</span
            >
          </div>
        </div>
      </div>
      <div
        v-if="showMapArea && (showMapTitle || showExportButton || showPriceToggle)"
        class="cms-map-widget__header"
      >
        <div v-if="showMapTitle" class="cms-map-widget__title">
          {{ mapTitle }}
        </div>
        <div class="cms-map-widget__header-actions">
          <div
            v-if="showPriceToggle"
            class="cms-map-widget__price-toggle"
            role="group"
            aria-label="Price display"
          >
            <button
              class="cms-map-widget__price-toggle-option"
              :class="{
                'cms-map-widget__price-toggle-option--active': activePriceUnit === 'percent',
              }"
              type="button"
              aria-label="Show prices as percent"
              :aria-pressed="activePriceUnit === 'percent' ? 'true' : 'false'"
              @click="setPriceUnit('percent')"
            >
              %
            </button>
            <button
              class="cms-map-widget__price-toggle-option"
              :class="{
                'cms-map-widget__price-toggle-option--active': activePriceUnit === 'cents',
              }"
              type="button"
              aria-label="Show prices in cents"
              :aria-pressed="activePriceUnit === 'cents' ? 'true' : 'false'"
              @click="setPriceUnit('cents')"
            >
              &cent;
            </button>
          </div>
          <button
            v-if="showExportButton"
            class="cms-map-widget__export"
            type="button"
            aria-label="Map options"
            @click="openExportMenu"
          >
            <span class="cms-map-widget__export-line"></span>
            <span class="cms-map-widget__export-line"></span>
            <span class="cms-map-widget__export-line"></span>
          </button>
        </div>
      </div>
      <div v-if="showMapArea" class="cms-map-widget__body">
        <div class="cms-map-widget__main">
          <div
            ref="chart"
            class="cms-map-widget__chart"
            :class="{ 'cms-map-widget__chart--offscreen': isTileMap }"
          ></div>
          <div v-if="isTileMap" ref="tileMap" class="cms-map-widget__tiles">
            <div class="cms-map-widget__tile-grid" :style="tileGridStyle">
              <button
                v-for="tile in mapTiles"
                :key="tile.stateCode"
                class="cms-map-widget__tile"
                :class="{ 'cms-map-widget__tile--active': tile.stateCode === activeTileCode }"
                type="button"
                :style="getTileStyle(tile)"
                :aria-label="getTileAriaLabel(tile)"
                :aria-expanded="tile.stateCode === activeTileCode ? 'true' : 'false'"
                @click="toggleTile(tile)"
              >
                <span>{{ tile.label }}</span>
                <span v-if="tile.price" class="cms-map-widget__tile-price">{{ tile.price }}</span>
              </button>
            </div>
            <div
              v-if="activeTileHtml"
              ref="tileCard"
              class="cms-map-widget__tile-card"
              @click="openTooltipMarket"
              v-html="activeTileHtml"
            ></div>
          </div>
          <div
            v-show="!showDataTable && !isTileMap"
            ref="zoomControl"
            class="cms-map-widget__zoom"
            @keydown.esc="closeZoomPanel"
          >
            <div
              v-if="isZoomPanelOpen"
              class="cms-map-widget__zoom-panel"
              role="group"
              aria-label="Map zoom"
            >
              <button
                class="cms-map-widget__zoom-step"
                type="button"
                aria-label="Zoom in"
                @click="zoomMapIn"
              >
                +
              </button>
              <span class="cms-map-widget__zoom-track">
                <input
                  class="cms-map-widget__zoom-range"
                  type="range"
                  min="0"
                  :max="zoomSliderMax"
                  step="0.01"
                  :value="zoomSliderValue"
                  :style="{ '--zoom-fill': zoomSliderFill }"
                  aria-label="Zoom level"
                  :aria-valuetext="zoomReadout"
                  @input="onZoomSliderInput"
                />
              </span>
              <button
                class="cms-map-widget__zoom-step"
                type="button"
                aria-label="Zoom out"
                @click="zoomMapOut"
              >
                &minus;
              </button>
              <span class="cms-map-widget__zoom-readout">{{ zoomReadout }}</span>
            </div>
            <button
              class="cms-map-widget__zoom-button"
              :class="{ 'cms-map-widget__zoom-button--active': isZoomPanelOpen }"
              type="button"
              aria-label="Zoom"
              :aria-expanded="isZoomPanelOpen ? 'true' : 'false'"
              @click="toggleZoomPanel"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="7" cy="7" r="4.75" stroke="currentColor" stroke-width="1.6" />
                <path
                  d="M10.5 10.5L14 14"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <button
              v-if="isMapZoomed"
              class="cms-map-widget__zoom-button"
              type="button"
              aria-label="Reset zoom"
              @click="resetMapZoom"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M3 8a5 5 0 1 0 1.6-3.7"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                />
                <path
                  d="M4.5 1.8v2.8h2.8"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
          <div
            v-if="showPinchHint && !isTileMap"
            class="cms-map-widget__pinch-hint"
            @click="dismissPinchHint"
          >
            Pinch to zoom
          </div>
          <span
            class="cms-map-widget__watermark cms-map-widget__watermark--map"
            aria-hidden="true"
          ></span>
        </div>
      </div>
      <div
        v-if="showMapArea && noMarketKeyItems.length > 0 && !keyInSummaryInfo"
        class="cms-map-widget__key"
      >
        <span v-for="item in noMarketKeyItems" :key="item.key" class="cms-map-widget__key-item">
          <span class="cms-map-widget__key-swatch" :style="{ background: item.swatch }"></span
          >{{ item.label }}
        </span>
      </div>
      <div v-if="showDataTable" ref="tablePanel" class="cms-map-widget__table-panel">
        <div class="cms-map-widget__table-header">
          <div class="cms-map-widget__table-title">Map data</div>
          <button class="cms-map-widget__table-close" type="button" @click="hideDataTable">
            Close
          </button>
        </div>
        <div class="cms-map-widget__table-scroll">
          <table v-if="mapTableRows.length > 0" class="cms-map-widget__table">
            <thead>
              <tr>
                <th>State</th>
                <th>Market</th>
                <th>Contract</th>
                <th>Price</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in mapTableRows" :key="row.key">
                <td>
                  <span
                    v-if="row.color"
                    class="cms-map-widget__table-swatch"
                    :style="{ backgroundColor: row.color }"
                  ></span>
                  {{ row.stateCode || '-' }}
                </td>
                <td>{{ row.marketName }}</td>
                <td>{{ row.contractName || '-' }}</td>
                <td class="cms-map-widget__table-price">
                  <span v-if="row.lastTradePrice !== null">{{
                    formatPrice(row.lastTradePrice)
                  }}</span>
                  <span v-else>-</span>
                </td>
                <td>{{ row.status || '-' }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="cms-map-widget__table-empty">No map data available.</div>
        </div>
      </div>

      <div v-if="isLoading" class="cms-map-widget__loading">Loading map...</div>
      <div v-if="showLegend" class="cms-map-widget__legend">
        <div v-for="item in legendItems" :key="item.key" class="cms-map-widget__legend-item">
          <span
            class="cms-map-widget__legend-swatch"
            :style="{ backgroundColor: item.color }"
          ></span>
          <span class="cms-map-widget__legend-name">{{ item.name }}</span>
          <span class="cms-map-widget__legend-price">{{ item.detail }}</span>
        </div>
        <div v-if="legendShadeNote" class="cms-map-widget__legend-item cms-map-widget__legend-note">
          {{ legendShadeNote }}
        </div>
      </div>
    </template>
  </div>
</template>

<script src="./map-widget-view.js"></script>
<style src="./map-widget-view.scss" lang="scss"></style>
